import type { ThemeOverrides } from '@okrapdf/design-system';
import type { BrandConfig } from './types';

export const OKRAPDF_BRAND: BrandConfig = {
  id: 'okrapdf',
  name: 'Okra',
  shortName: 'Okra',
  domain: 'okrapdf.com',
  cookieDomain: '.okrapdf.com',
  hosts: {
    apex: ['okrapdf.com'],
    www: ['www.okrapdf.com'],
    app: ['app.okrapdf.com'],
  },
  origins: {
    marketing: 'https://okrapdf.com',
    app: 'https://app.okrapdf.com',
    api: 'https://api.okrapdf.com',
  },
  assets: {
    logo: '/okra-logo-120.png',
    favicon: '/favicon.ico',
    appleTouchIcon: '/apple-touch-icon.png',
    ogImage: '/og-image.jpg',
  },
  seo: {
    siteName: 'Okra',
    defaultTitle: 'Okra — Document tools and workflows for document-heavy teams',
    defaultDescription:
      'Read, extract, audit, and automate documents for accounting, logistics, legal, and real estate. Start with a free tool, run locally, or connect the API.',
  },
  supportEmail: 'support@okrapdf.com',
  gaMeasurementId: 'G-5W72M4QEVS',
};

/** Cooler cyan-teal “grid” palette — distinct from okra green, not the banned indigo/navy. */
const PARSEGRID_THEME = {
  brand: {
    iris: { hex: '#0F7A8A', darkHex: '#2DD4BF' },
    irisHover: { hex: '#0C6573', darkHex: '#5EEAD4' },
    leaf: { hex: '#14B8A6', darkHex: '#5EEAD4' },
  },
  surface: {
    page: { hex: '#F7F8FA', darkHex: '#121417' },
    raised: { hex: '#ffffff', darkHex: '#1A1D22' },
    sunk: { hex: '#EEF1F4', darkHex: '#0E1014' },
    ink: { hex: '#16181D', darkHex: '#F4F6F8' },
    inkSoft: { hex: '#4B5563', darkHex: '#C5CBD3' },
    inkMute: { hex: '#6B7280', darkHex: '#8B929C' },
    line: { hex: '#E2E8F0', darkHex: 'rgba(244,246,248,0.12)' },
    lineSoft: { hex: '#EEF2F6', darkHex: 'rgba(244,246,248,0.07)' },
    accent: { hex: '#0F7A8A', darkHex: '#2DD4BF' },
    accentText: { hex: '#0B5F6B', darkHex: '#99F6E4' },
    accentTint: { hex: '#E6F6F8', darkHex: 'rgba(45,212,191,0.16)' },
    brandLeaf: { hex: '#14B8A6', darkHex: '#5EEAD4' },
    selected: { hex: '#E6F6F8', darkHex: 'rgba(45,212,191,0.12)' },
    selectedLine: { hex: '#B8DEE4', darkHex: 'rgba(45,212,191,0.42)' },
  },
} satisfies ThemeOverrides;

export const PARSEGRID_BRAND: BrandConfig = {
  id: 'parsegrid',
  name: 'ParseGrid',
  shortName: 'ParseGrid',
  domain: 'parsegrid.ai',
  cookieDomain: '.parsegrid.ai',
  hosts: {
    apex: ['parsegrid.ai'],
    www: ['www.parsegrid.ai'],
    app: ['app.parsegrid.ai'],
  },
  origins: {
    marketing: 'https://parsegrid.ai',
    app: 'https://app.parsegrid.ai',
    api: 'https://api.okrapdf.com',
  },
  assets: {
    logo: '/parsegrid-logo.svg',
    favicon: '/parsegrid-logo.svg',
    appleTouchIcon: '/parsegrid-apple-touch-icon.svg',
    ogImage: '/parsegrid-logo.svg',
  },
  seo: {
    siteName: 'ParseGrid',
    defaultTitle: 'ParseGrid — Document tools and workflows for document-heavy teams',
    defaultDescription:
      'Read, extract, audit, and automate documents for accounting, logistics, legal, and real estate. Start with a free tool, run locally, or connect the API.',
  },
  supportEmail: 'support@parsegrid.ai',
  theme: PARSEGRID_THEME,
};

export const BRANDS: Record<BrandConfig['id'], BrandConfig> = {
  okrapdf: OKRAPDF_BRAND,
  parsegrid: PARSEGRID_BRAND,
};
